/**
 * TenantLivePickupCard — Uber-style live status card on the Tenant dashboard.
 * Shows when a driver is currently running a route that includes the tenant's property:
 *   - "Truck N stops away · ~X min"
 *   - "First pickup started in your building" once the driver marks arrived
 *   - "Pickup complete" when the property's stop is done
 * Auto-refreshes every 30s.
 */
import { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Truck, MapPin, CheckCircle2, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30000;

export default function TenantLivePickupCard() {
  const [eta, setEta] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/tracking/eta-for-tenant`, { credentials: 'include' });
      if (res.ok) setEta(await res.json());
    } catch (_) { /* noop */ }
    finally { setLoaded(true); }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  // Hide entirely when no active session for this tenant's property.
  if (!loaded || !eta?.active) return null;

  const status = eta.my_status;
  const isCompleted = status === 'completed';
  const isArrived = status === 'arrived';

  // Tone the card based on status
  const accent = isCompleted ? 'border-emerald-500/60' : isArrived ? 'border-[#FFD60A]' : 'border-blue-500/50';
  const headerIconColor = isCompleted ? 'text-emerald-400' : isArrived ? 'text-[#FFD60A]' : 'text-blue-400';

  return (
    <Card className={`bg-zinc-900 ${accent} border-2 p-5 relative overflow-hidden`} data-testid="tenant-live-pickup-card">
      {/* Soft pulse for active state */}
      {!isCompleted && (
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#FFD60A]/10 blur-3xl animate-pulse pointer-events-none" />
      )}

      <div className="flex items-start justify-between flex-wrap gap-3 relative">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center ${headerIconColor}`}>
            {isCompleted ? <CheckCircle2 className="h-6 w-6" /> : <Truck className={`h-6 w-6 ${!isCompleted ? 'animate-pulse' : ''}`} />}
          </div>
          <div>
            <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {isCompleted ? 'Pickup complete in your building' :
                isArrived ? 'Pickup in progress in your building' :
                'Live pickup tracking'}
            </h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Driver: {eta.driver_name} · Route: {eta.route_name}
            </p>
          </div>
        </div>
        {isCompleted && <Badge className="bg-emerald-600 text-white">Done</Badge>}
        {isArrived && <Badge className="bg-[#FFD60A] text-black animate-pulse" data-testid="tenant-live-arrived-badge">In your building</Badge>}
        {!isArrived && !isCompleted && <Badge className="bg-blue-600 text-white">En route</Badge>}
      </div>

      {/* Big stat strip */}
      <div className="mt-4 grid grid-cols-3 gap-3 relative">
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-center" data-testid="tenant-live-stops-away">
          <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Stops away</div>
          <div className="text-3xl font-bold text-white mt-1">{isArrived || isCompleted ? '0' : eta.stops_away}</div>
        </div>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-center" data-testid="tenant-live-eta">
          <div className="text-[10px] text-zinc-400 uppercase tracking-wider flex items-center justify-center gap-1">
            <Clock className="h-3 w-3" /> ETA
          </div>
          <div className="text-3xl font-bold text-white mt-1">
            {isCompleted ? '—' : isArrived ? 'Now' : `~${eta.eta_minutes}m`}
          </div>
        </div>
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-center">
          <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Route progress</div>
          <div className="text-3xl font-bold text-white mt-1">{eta.stops_completed}/{eta.stops_total}</div>
        </div>
      </div>

      {/* First pickup banner */}
      {eta.first_pickup_started && !isCompleted && (
        <div className="mt-3 flex items-center gap-2 text-[12px] bg-emerald-900/30 border border-emerald-500/40 rounded-lg p-2.5 text-emerald-300" data-testid="tenant-live-first-pickup-banner">
          <MapPin className="h-4 w-4" />
          First pickup has started in your building
          {eta.arrived_at && <span className="text-emerald-400/80 ml-auto">since {new Date(eta.arrived_at).toLocaleTimeString()}</span>}
        </div>
      )}

      {/* Bring trash can inside banner — fires when status becomes 'completed' */}
      {isCompleted && (
        <div
          className="mt-3 flex items-start gap-2 text-[12px] rounded-lg p-2.5"
          style={{ backgroundColor: 'rgba(255, 214, 10, 0.15)', border: '1px solid rgba(255, 214, 10, 0.4)', color: '#FFD60A' }}
          data-testid="tenant-live-bring-inside-banner"
        >
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold uppercase tracking-wider text-[10px]">Action needed</p>
            <p>Please bring your trash can back inside your apartment now — no later than 8:00 AM.</p>
          </div>
        </div>
      )}

      <p className="text-[10px] text-zinc-500 mt-3">
        Auto-updates every 30 seconds while a driver is active.
      </p>
    </Card>
  );
}
